import { useEffect, useMemo, useRef, useState } from "react";
import PageUploadAudioAPIService from "../../service/PageUploadAudioAPIService";

/**
 * One pipeline node entry from the `/audio/progress` status `steps` map. The backend owns
 * which nodes a workflow has, their display label and their order.
 */
interface StatusStepEntry {
  id?: string;
  status?: string;
  label?: string;
  order?: number;
}

/** Older records may still nest a step's entry under a config uid. */
type StatusStepValue = StatusStepEntry | Record<string, StatusStepEntry>;
export type StatusStepsMap = Partial<Record<string, StatusStepValue>>;

interface AudioProgressResponse {
  status?: string;
  fileId?: string;
  fileName?: string;
  url?: string;
  preprocessedUrl?: string;
  steps?: StatusStepsMap;
}

export type StepUiState = "completed" | "error" | "in_progress" | "pending";

/**
 * Overall workflow lifecycle derived from the individual step states.
 * - `pending`: every step is still pending (nothing started)
 * - `running`: at least one step is in progress / partially done
 * - `succeeded`: every step reported by the API completed
 * - `failed`: at least one step failed
 * - `idle`: nothing is being tracked
 */
export type WorkflowStatus = "idle" | "pending" | "running" | "succeeded" | "failed"|"success";

export interface PipelineStepProgress {
  /** Step key as reported in the `steps` map. */
  key: string;
  label: string;
  state: StepUiState;
}

/** Interval between successive `/audio/progress` polls while a workflow is running. */
const POLL_INTERVAL_MS = 3000;

/**
 * Resolve a step's entry from the steps map. Most steps are a flat
 * `{id, status, label, order}`, but config-keyed steps (e.g. analyze_qc) carry `label` and
 * `order` at the top level with `{id, status}` nested under the config uid. Merge the two so
 * the step keeps its label and position; the first nested entry carrying a status is used.
 */
const getStepEntry = (value: StatusStepValue | undefined): StatusStepEntry | null => {
  if (!value) return null;
  const outer = value as StatusStepEntry;
  if (typeof outer.status === "string") return outer;
  const firstNested = Object.values(value as Record<string, unknown>).find(
    (entry): entry is StatusStepEntry =>
      !!entry && typeof entry === "object" && typeof (entry as StatusStepEntry).status === "string"
  );
  if (!firstNested) return null;
  return { ...firstNested, label: outer.label ?? firstNested.label, order: outer.order ?? firstNested.order };
};

/** Fallback label for an entry without one, e.g. "analyze_qc" -> "Analyze qc". */
const humanizeStepKey = (key: string): string => {
  const words = key.split("_").filter(Boolean).join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Status strings arrive capitalized and space-separated (e.g. "Completed", "Not Started",
 * "In Progress"); normalize to snake_case for UI rendering.
 */
const normalizeStepState = (status: string | null): StepUiState => {
  if (!status) return "pending";
  const normalized = status.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (["completed", "complete", "success", "succeeded", "done"].includes(normalized)) {
    return "completed";
  }
  if (["error", "failed", "failure"].includes(normalized)) {
    return "error";
  }
  // Registry-bound steps the workflow hasn't reached yet arrive with a not-started status.
  if (["pending", "queued", "not_started", "waiting"].includes(normalized)) {
    return "pending";
  }
  return "in_progress";
};

/** Per-step icon and text presentation, keyed by design tokens rather than raw colors. */
export const STEP_UI: Record<
  StepUiState,
  { icon: string; iconClass: string; labelClass: string; caption?: string }
> = {
  completed: {
    icon: "pi-check-circle",
    iconClass: "text-icon-success",
    labelClass: "text-text-primary",
  },
  error: {
    icon: "pi-times-circle",
    iconClass: "text-icon-error",
    labelClass: "text-text-error",
    caption: "Failed",
  },
  in_progress: {
    icon: "pi-spinner pi-spin",
    iconClass: "text-icon-info",
    labelClass: "text-text-primary",
    caption: "In progress",
  },
  pending: {
    icon: "pi-circle",
    iconClass: "text-icon-disabled",
    labelClass: "text-text-disabled",
  },
};

/** Presentation for the overall workflow status (label + SDK Badge state). */
export const WORKFLOW_STATUS_UI: Record<
  Exclude<WorkflowStatus, "idle">,
  { label: string; badgeState: "Neutral" | "Info" | "Success" | "Error" }
> = {
  pending: { label: "Pending", badgeState: "Neutral" },
  running: { label: "In Progress", badgeState: "Info" },
  succeeded: { label: "Success", badgeState: "Success" },
  success: { label: "Success", badgeState: "Success" },
  failed: { label: "Failed", badgeState: "Error" },
};

export interface WorkflowStatusSummary {
  /** Per-step checklist progress, ordered for rendering. */
  pipelineProgress: PipelineStepProgress[];
  /** Count of steps that have reached "Completed". */
  completedCount: number;
  /** Number of steps the API response reports for this workflow. */
  totalCount: number;
  /** True when at least one step has failed. */
  hasError: boolean;
  /** True when every reported step has completed. */
  isComplete: boolean;
  /** Overall lifecycle of the workflow (never "idle" — a steps map is always a tracked workflow). */
  status: Exclude<WorkflowStatus, "idle">;
}

/**
 * Pure derivation of per-step progress and overall status from a raw `/audio/progress`
 * (or `/workflows/status`) steps map. Shared by the live tracker hook and any list that
 * needs to label a workflow record (e.g. the Recent Audio panel).
 */
export function deriveWorkflowStatus(steps: StatusStepsMap): WorkflowStatusSummary {
  // The response decides the steps, their labels and their order; the stable sort keeps
  // entries without an `order` in response order after the ordered ones.
  const pipelineProgress: PipelineStepProgress[] = Object.entries(steps ?? {})
    .filter(([, value]) => value != null)
    .map(([key, value]) => ({ key, entry: getStepEntry(value) }))
    .sort(
      (a, b) =>
        (a.entry?.order ?? Number.POSITIVE_INFINITY) - (b.entry?.order ?? Number.POSITIVE_INFINITY)
    )
    .map(({ key, entry }) => ({
      key,
      label: entry?.label || humanizeStepKey(key),
      state: normalizeStepState(entry?.status ?? null),
    }));

  const totalCount = pipelineProgress.length;
  const completedCount = pipelineProgress.filter((step) => step.state === "completed").length;
  const hasError = pipelineProgress.some((step) => step.state === "error");
  // With no steps reported yet there is nothing to have completed.
  const isComplete = totalCount > 0 && completedCount >= totalCount;
  const allPending = pipelineProgress.every((step) => step.state === "pending");

  let status: WorkflowStatusSummary["status"];
  if (hasError) status = "failed";
  else if (isComplete) status = "succeeded";
  else if (allPending) status = "pending";
  else status = "running";

  return { pipelineProgress, completedCount, totalCount, hasError, isComplete, status };
}

export interface AudioWorkflowStatus {
  /** Per-step checklist progress, ordered for rendering. */
  pipelineProgress: PipelineStepProgress[];
  /** Count of steps that have reached "Completed". */
  completedCount: number;
  /** Number of steps the API response reports for this workflow. */
  totalCount: number;
  /** True while a poll request is in flight. */
  progressLoading: boolean;
  /** True once the first poll for the current workflow has finished (successfully or not). */
  hasLoaded: boolean;
  /** File id reported by the workflow once the file record exists. */
  fileId: string;
  /** Preprocessed audio url reported by the workflow. */
  processedUrl: string;
  /** True when at least one step has failed. */
  hasError: boolean;
  /** True when every reported step has completed. */
  isComplete: boolean;
  /** Overall lifecycle of the workflow. */
  status: WorkflowStatus;
}

/**
 * Long-polls the `/audio/progress` endpoint for a given status id (the audio url that
 * identifies a triggered workflow) and derives the workflow's overall state.
 *
 * Polling stops automatically once every reported step has completed or any step fails,
 * so the same hook can drive both the post-upload flow and a `statusId`-resumed page.
 *
 * @param statusId The audio url identifying the workflow. Empty disables polling.
 */
export default function useAudioWorkflowStatus(statusId: string): AudioWorkflowStatus {
  const [steps, setSteps] = useState<StatusStepsMap>({});
  const [progressLoading, setProgressLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fileId, setFileId] = useState("");
  const [processedUrl, setProcessedUrl] = useState("");
  const stoppedRef = useRef(false);

  useEffect(() => {
    // Reset derived state whenever the workflow we're tracking changes.
    setSteps({});
    setHasLoaded(false);
    setFileId("");
    setProcessedUrl("");
    stoppedRef.current = false;

    if (!statusId) {
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const fetchProgress = async () => {
      if (cancelled) return;
      try {
        setProgressLoading(true);
        const progress: AudioProgressResponse | undefined =
          await PageUploadAudioAPIService.getAudioProgress(statusId);
        if (cancelled || !progress) return;
        if (progress.fileId) {
          // The upload endpoint no longer returns a file id; it appears here once
          // the workflow creates the file record.
          setFileId(progress.fileId);
        }
        setProcessedUrl(progress.preprocessedUrl || "");
        const nextSteps = progress.steps || {};
        setSteps(nextSteps);

        // Stop long-polling once the workflow has succeeded or failed.
        const { isComplete, hasError } = deriveWorkflowStatus(nextSteps);
        if (isComplete || hasError) {
          stoppedRef.current = true;
          stopPolling();
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error polling audio progress:", error);
        }
      } finally {
        if (!cancelled) {
          setProgressLoading(false);
          setHasLoaded(true);
        }
      }
    };

    fetchProgress();
    intervalId = setInterval(fetchProgress, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [statusId]);

  const summary = useMemo(() => deriveWorkflowStatus(steps), [steps]);

  // Without a tracked workflow there is nothing to report.
  const status: WorkflowStatus = statusId ? summary.status : "idle";

  return {
    ...summary,
    progressLoading,
    hasLoaded,
    fileId,
    processedUrl,
    status,
  };
}
